import React, {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
} from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import type { BrandingConfig, ThemeTokens } from './tokens';
import {
    DEFAULT_BRANDING,
    DEFAULT_THEME_TOKENS,
    THEME_PRESETS,
} from './tokens';
import {
    applyThemeTokens,
    loadBrandingConfig,
    resetBrandingConfig,
    saveBrandingConfig,
} from './applyTheme';
import { createAppTheme } from './createAppTheme';

interface ThemeContextValue {
    branding: BrandingConfig;
    tokens: ThemeTokens;
    brandImageUrl: string | null;
    setToken: <K extends keyof ThemeTokens>(key: K, value: ThemeTokens[K]) => void;
    setTokens: (tokens: Partial<ThemeTokens>) => void;
    applyPreset: (presetKey: keyof typeof THEME_PRESETS) => void;
    setBrandImageUrl: (url: string | null) => void;
    saveBranding: (config?: BrandingConfig) => void;
    resetBranding: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const persistAndApply = (config: BrandingConfig) => {
    applyThemeTokens(config.tokens);
    saveBrandingConfig(config);
};

export const AppThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [branding, setBranding] = useState<BrandingConfig>(() => loadBrandingConfig());

    const muiTheme = useMemo(
        () => createAppTheme(branding.tokens),
        [branding.tokens],
    );

    const updateBranding = useCallback((updater: (prev: BrandingConfig) => BrandingConfig) => {
        setBranding((prev) => {
            const next = updater(prev);
            persistAndApply(next);
            return next;
        });
    }, []);

    const setToken = useCallback(<K extends keyof ThemeTokens>(key: K, value: ThemeTokens[K]) => {
        updateBranding((prev) => ({
            ...prev,
            tokens: { ...prev.tokens, [key]: value },
        }));
    }, [updateBranding]);

    const setTokens = useCallback((tokens: Partial<ThemeTokens>) => {
        updateBranding((prev) => ({
            ...prev,
            tokens: { ...prev.tokens, ...tokens },
        }));
    }, [updateBranding]);

    const applyPreset = useCallback((presetKey: keyof typeof THEME_PRESETS) => {
        updateBranding((prev) => ({
            ...prev,
            tokens: { ...THEME_PRESETS[presetKey] },
        }));
    }, [updateBranding]);

    const setBrandImageUrl = useCallback((url: string | null) => {
        updateBranding((prev) => ({
            ...prev,
            brandImageUrl: url,
        }));
    }, [updateBranding]);

    const saveBranding = useCallback((config?: BrandingConfig) => {
        setBranding((prev) => {
            const next = config || prev;
            persistAndApply(next);
            return next;
        });
    }, []);

    const resetBranding = useCallback(() => {
        const next = resetBrandingConfig();
        setBranding(next);
    }, []);

    const value = useMemo<ThemeContextValue>(() => ({
        branding,
        tokens: branding.tokens,
        brandImageUrl: branding.brandImageUrl,
        setToken,
        setTokens,
        applyPreset,
        setBrandImageUrl,
        saveBranding,
        resetBranding,
    }), [branding, setToken, setTokens, applyPreset, setBrandImageUrl, saveBranding, resetBranding]);

    return (
        <ThemeContext.Provider value={value}>
            <MuiThemeProvider theme={muiTheme}>{children}</MuiThemeProvider>
        </ThemeContext.Provider>
    );
};

export const useAppTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useAppTheme must be used within AppThemeProvider');
    }
    return context;
};

export const useBrandLogo = (fallback: string) =>
    useAppTheme().brandImageUrl || fallback;

export { DEFAULT_BRANDING, DEFAULT_THEME_TOKENS, THEME_PRESETS };
